import { RouteChildNodeDataType, RouteNodeType, RouteParentNodeDataType } from '@lp/features/RoutePlanner/types';
import { CaseResourceType } from '@/api/apis/case';
import {
  ADDITIONAL_SCHEMA_HEIGHT,
  ALT_PADDING_WIDTH,
  CHARACTER_WIDTH,
  CHILDREN_NODES_MARGIN,
  DEFAULT_SCHEMA_HEIGHT,
  ICON_BLOCK_WIDTH,
  STEP_EDGE_WIDTH,
} from './consts';

const calculateBlockHeight = (hasRepositionFurther: boolean, hasBackToBaseEarlier: boolean): number => {
  let blockHeight = DEFAULT_SCHEMA_HEIGHT;

  if (hasRepositionFurther) {
    blockHeight += ADDITIONAL_SCHEMA_HEIGHT;
  }

  if (hasBackToBaseEarlier) {
    blockHeight += ADDITIONAL_SCHEMA_HEIGHT;
  }

  return blockHeight;
};

export const calculateNodeRect = (
  childNodes: RouteNodeType<RouteChildNodeDataType>[],
): { mainNodeY: number; blockHeight: number } => {
  const hasTop = childNodes.some((n) => n.data.connectionType === 'to');
  const hasBottom = childNodes.some((n) => n.data.connectionType === 'from');
  const blockHeight = calculateBlockHeight(hasTop, hasBottom);

  let mainNodeY = blockHeight / 2;

  if (hasTop && childNodes.length) {
    mainNodeY = 95;
  }

  if (hasBottom && childNodes.length) {
    mainNodeY = 30;
  }

  if (hasTop && hasBottom && childNodes.length) {
    mainNodeY = blockHeight / 2;
  }

  return { mainNodeY, blockHeight };
};

export const getUpdatedNode = <T = RouteParentNodeDataType>(
  node: RouteNodeType<T>,
  prevNodeX: number,
  mainNodeY: number,
  schemaType: 'main' | 'alt' = 'main',
): RouteNodeType<T> => {
  const isMainSchema = schemaType === 'main';
  const nodeTextTitle = node.data.title || '';
  const nodeTextLength = nodeTextTitle.length;
  const nodeWidth = isMainSchema
    ? ICON_BLOCK_WIDTH + nodeTextLength * CHARACTER_WIDTH
    : ALT_PADDING_WIDTH + nodeTextLength * CHARACTER_WIDTH;

  return {
    ...node,
    width: nodeWidth,
    position: {
      x: prevNodeX,
      y: mainNodeY,
    },
  };
};

type GetUpdatedChildNodeParams = {
  node: RouteNodeType<RouteChildNodeDataType>;
  parentNodeX: number;
  nodeY: number;
  index: number;
  parentNodeWidth: number;
  resource?: CaseResourceType;
};

export const getUpdatedChildNode = ({
  node,
  parentNodeX,
  nodeY,
  index,
  parentNodeWidth,
  resource,
}: GetUpdatedChildNodeParams): RouteNodeType<RouteChildNodeDataType> => {
  const baseNode = getUpdatedNode<RouteChildNodeDataType>(node, parentNodeX, nodeY);
  const isTopChild = node.data.connectionType === 'to';
  let xPosition: number;
  let yPosition;

  switch (index) {
    case 1:
      xPosition = parentNodeX + parentNodeWidth + (STEP_EDGE_WIDTH * 1.2) / 2;
      yPosition = nodeY;
      break;
    case 2:
      xPosition = Math.round(parentNodeX + (parentNodeWidth - baseNode.width) / 2) + 0.5;
      yPosition = isTopChild ? nodeY - 20 : nodeY + 20;
      break;

    default:
      xPosition = parentNodeX - baseNode.width - (STEP_EDGE_WIDTH * 1.2) / 2;
      yPosition = nodeY;
  }

  return {
    ...baseNode,
    data: {
      ...baseNode.data,
      resource,
    },
    position: {
      x: xPosition,
      y: yPosition,
    },
  };
};

export const isNodesIntersecting = (leftNodeRect: number[], rightNodeRect: number[]) => {
  return leftNodeRect[0] <= rightNodeRect[1] && rightNodeRect[0] <= leftNodeRect[1];
};

const getNodeEdges = (node: RouteNodeType<RouteChildNodeDataType>, parentNode: RouteNodeType) => {
  const childLeftEdge = node.position.x;
  const childRightEdge = childLeftEdge + node.width;
  const parentLeftEdge = parentNode.position.x;
  const parentRightEdge = parentNode.position.x + parentNode.width;
  const parentLeftConnection = parentLeftEdge + parentNode.width / 3;
  const parentRightConnection = parentRightEdge - parentNode.width / 3;

  const leftEdge = parentRightConnection > childLeftEdge ? childLeftEdge : parentRightConnection;
  const rightEdge = parentLeftConnection > childRightEdge ? parentLeftConnection : childRightEdge;

  return [leftEdge, rightEdge];
};

export const getIntersectingChildNodes = (
  childNodes: RouteNodeType<RouteChildNodeDataType>[],
  parentNodes: RouteNodeType[],
): RouteNodeType<RouteChildNodeDataType>[] => {
  const parentNodeById = new Map<string | null | undefined, RouteNodeType>(parentNodes.map((p) => [p.id, p]));

  const edgesByNodeId = new Map(
    childNodes.map((n) => [n.id, getNodeEdges(n, parentNodeById.get(n.data.parentId)!)] as const),
  );

  return childNodes.filter((n) => {
    const [leftEdge, rightEdge] = edgesByNodeId.get(n.id)!;

    return childNodes.some((node) => {
      if (n.id === node.id) return false;

      const [currentNodeLeftEdge, currentNodeRightEdge] = edgesByNodeId.get(node.id)!;

      return isNodesIntersecting([leftEdge, rightEdge], [currentNodeLeftEdge, currentNodeRightEdge]);
    });
  });
};

export const getIntersectingChildNodesDiff = (childNodes: RouteNodeType<RouteChildNodeDataType>[]): number[] => {
  return childNodes.reduce((acc, n, index, array) => {
    if (!index) return acc;

    const prevNode = array[index - 1];
    const prevNodeRight = prevNode.position.x + prevNode.width;
    const currentNodeLeft = n.position.x;

    const diff = Math.abs(prevNodeRight - currentNodeLeft);

    return diff ? [...acc, diff] : acc;
  }, [] as number[]);
};

export const moveNodesToStart = (
  nodes: (RouteNodeType | RouteNodeType<RouteChildNodeDataType>)[],
): (RouteNodeType | RouteNodeType<RouteChildNodeDataType>)[] => {
  const leftPosition = nodes.reduce((acc, n) => {
    return n.position.x < acc ? n.position.x : acc;
  }, 0);

  if (leftPosition < 0) {
    return nodes.map((n) => ({
      ...n,
      position: {
        ...n.position,
        x: n.position.x + Math.abs(leftPosition),
      },
    }));
  }

  return nodes;
};

export const normalizeNodesPositions = (
  parentNodes: RouteNodeType[],
  childNodes: RouteNodeType<RouteChildNodeDataType>[],
): (RouteNodeType | RouteNodeType<RouteChildNodeDataType>)[] => {
  const intersectedTopChildNodes = getIntersectingChildNodes(
    childNodes.filter((n) => n.data.connectionType === 'to'),
    parentNodes,
  );
  const intersectedBottomChildNodes = getIntersectingChildNodes(
    childNodes.filter((n) => n.data.connectionType === 'from'),
    parentNodes,
  );

  const topNodesDiff = getIntersectingChildNodesDiff(intersectedTopChildNodes);
  const bottomNodesDiff = getIntersectingChildNodesDiff(intersectedBottomChildNodes);
  const maxDiff = Math.max(...topNodesDiff, ...bottomNodesDiff);
  const allIntersectedNodes = [...intersectedTopChildNodes, ...intersectedBottomChildNodes];
  const firstIntersectedRightNode = allIntersectedNodes.length > 1 ? allIntersectedNodes[1] : null;

  if (!maxDiff || !firstIntersectedRightNode) {
    return moveNodesToStart([...parentNodes, ...childNodes]);
  }

  const parentNodeId = firstIntersectedRightNode.data.parentId;
  const parentNodeIndex = parentNodes.findIndex((n) => n.id === parentNodeId);
  const firstChildNodeIndex = childNodes.findIndex((n) => n.data.parentId === parentNodeId);

  const movedParents = parentNodes.map((n, index) => {
    if (index >= parentNodeIndex) {
      return {
        ...n,
        position: {
          ...n.position,
          x: n.position.x + maxDiff + CHILDREN_NODES_MARGIN,
        },
      };
    }

    return n;
  });

  const movedChildren = childNodes.map((n, index) => {
    if (index >= firstChildNodeIndex) {
      return {
        ...n,
        position: {
          ...n.position,
          x: n.position.x + maxDiff + CHILDREN_NODES_MARGIN,
        },
      };
    }

    return n;
  });

  return moveNodesToStart([...movedParents, ...movedChildren]);
};
